import { CompanyActionPlanInvitation } from "src/components/Actions/ActionPlanInvitation/CompanyActionPlanInvitation";
import { ACTION_PLAN, CREATE, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { Typography } from "@foodpilot/foods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogTitle, Dialog, DialogContent, DialogActions, Button, Box, IconButton } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { useLockUnlockPost } from "src/api/client-api/post/updatePost.ts";
import { useSnackbar } from "src/utils/useSnackbar.tsx";
import { ValidationErrorsModal } from "./ValidationErrorsModal";
import { ConfirmWarningModal } from "./ConfirmWarningModal";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { useQuestionnaireListContext } from "../context/useQuestionnaireList";
import { useLocation } from "react-router";
import { whereAmI } from "src/services/urlSwitcher.ts";

export const LockPostModal = () => {
    const { isLockPostModalOpen, setLockPostModalOpen, post, setLocked, isLocked, ssqVersion } =
        useQuestionnaireListContext();
    const location = useLocation();
    const { canUser } = useFoodpilotContext();
    const { isActionPlanSimplified, isFeatureActivated } = useApplicationParameters();
    const [invitationOpen, setInvitationOpen] = useState(false);

    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const [validationMessages, setValidationMessages] = useState<ValidationMessage[]>([]);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [isConfirmWarningModalOpen, setIsConfirmWarningModalOpen] = useState(false);

    const lockUnlockPost = useLockUnlockPost({
        onValidationError: (messages) => {
            setLockPostModalOpen(false);
            setValidationMessages(messages);
            setIsValidationModalOpen(true);
        },
        onValidationWarning: (messages) => {
            setLockPostModalOpen(false);
            setValidationMessages(messages);
            setIsConfirmWarningModalOpen(true);
        },
    });

    const onCloseAndReset = () => {
        setLockPostModalOpen(false);
    };

    if (post === undefined) return;

    const onConfirm = (forceLock: boolean = false) => {
        lockUnlockPost.mutate(
            { post: post, isLocked: isLocked, forceLock: forceLock },
            {
                onSuccess: () => {
                    setLockPostModalOpen(false);
                    setIsConfirmWarningModalOpen(false);
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "success",
                        message:
                            isLocked ?
                                t("questionnaire.unlock_questionnaire.snackbar.success")
                            :   t("questionnaire.lock_questionnaire.snackbar.success"),
                    });
                    setLocked(!isLocked);
                    if (
                        !isLocked &&
                        whereAmI({ url: location.pathname }).inCompany &&
                        isActionPlanSimplified() &&
                        isFeatureActivated("FS-actionPlan") &&
                        canUser(CREATE, ACTION_PLAN)
                    ) {
                        setInvitationOpen(true);
                    }
                },
            },
        );
    };

    const myLocation = whereAmI({ url: location.pathname });
    let invalidationMessage: string;
    if (myLocation.inProduct) {
        invalidationMessage = t(
            "questionnaire.unlock_questionnaire.modal_body.product",
            "Invalidating this questionnaire will impact the calculation of product indicators.",
        );
    } else if (myLocation.inIngredients) {
        invalidationMessage = t(
            "questionnaire.unlock_questionnaire.modal_body.ingredient",
            "Invalidating this questionnaire will impact the calculation of indicators for this ingredient and any related products.",
        );
    } else if (myLocation.inPackagings) {
        invalidationMessage = t(
            "questionnaire.unlock_questionnaire.modal_body.packaging",
            "Invalidating this questionnaire will impact the calculation of packaging indicators.",
        );
    } else if (myLocation.inCompany) {
        invalidationMessage =
            post.isUsedInActionPlan ?
                t(
                    "questionnaire.unlock_questionnaire.modal_body.company_with_action_plan",
                    "This diagnostic questionnaire is used as a reference for your action plan.\nAny change will impact your existing action plan.\nYou will be able to choose how to handle these changes:\n- Recalculate all actions based on the updated diagnostic\n- Or delete and recreate your action plan",
                )
            :   t(
                    "questionnaire.unlock_questionnaire.modal_body.company_no_action_plan",
                    "Invalidating this questionnaire will impact the calculation of your indicators. Changes will be reflected in your results once the questionnaire is updated.",
                );
    } else {
        invalidationMessage = t(
            "questionnaire.unlock_questionnaire.modal_body.default",
            "You are about to invalidate this questionnaire",
        );
    }

    let invalidationTitle;
    if (myLocation.inCompany && post.isUsedInActionPlan) {
        invalidationTitle = t(
            "questionnaire.unlock_questionnaire.modal_title.company_with_action_plan",
            "Invalidating this questionnaire will impact your action plan",
        );
    } else {
        invalidationTitle = t(
            "questionnaire.unlock_questionnaire.modal_title.default",
            "You are about to invalidate this questionnaire",
        );
    }

    return (
        <>
            {invitationOpen && (
                <CompanyActionPlanInvitation
                    post={post}
                    ssqTitle={ssqVersion?.ssq.title}
                    onClose={() => setInvitationOpen(false)}
                />
            )}
            <Dialog maxWidth="xs" open={isLockPostModalOpen}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h3">
                            {isLocked ? invalidationTitle : t("questionnaire.lock_questionnaire.modal_title")}
                        </Typography>
                        <IconButton onClick={onCloseAndReset} edge="end" size="small" aria-label="close">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body-medium" sx={{ whiteSpace: "pre-line" }}>
                        {isLocked ? invalidationMessage : t("questionnaire.lock_questionnaire.modal_body")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Box display="flex" flexDirection="column" width="100%" gap={1} px={2} pb={2}>
                        <Button onClick={() => onConfirm(false)} variant="primary">
                            {isLocked ?
                                t("questionnaire.lock_questionnaire.modal_button_confirm_unlock")
                            :   t("questionnaire.lock_questionnaire.modal_button_confirm_lock")}
                        </Button>
                        <Button onClick={onCloseAndReset} variant="ternary">
                            {isLocked ?
                                t("questionnaire.lock_questionnaire.modal_button_cancel_unlock")
                            :   t("questionnaire.lock_questionnaire.modal_button_cancel_lock")}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <ValidationErrorsModal
                isOpen={isValidationModalOpen}
                onClose={() => setIsValidationModalOpen(false)}
                validationMessages={validationMessages}
                context={{ ssqVersion }}
            />

            <ConfirmWarningModal
                isOpen={isConfirmWarningModalOpen}
                onClose={() => setIsConfirmWarningModalOpen(false)}
                onConfirm={onConfirm}
                validationMessages={validationMessages}
            />
        </>
    );
};
